/**
 * Single Unit Orchestrator
 *
 * Coordinates rendering of single-unit community buildings.
 * Applies building transformation (position, rotation) to the rendered unit.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { GeneratedBuilding, GeneratedUnit } from '@/lib/templates/types'
import { renderUnit, type UnitRenderOptions } from '../composers/RectangularUnitComposer'

/**
 * Building render options
 */
export interface BuildingRenderOptions extends UnitRenderOptions {
  /** Building ID for userData */
  buildingId?: string
  /** Building name for userData */
  buildingName?: string
  /** Position in feet [x, y, z] relative to scene center */
  position?: [number, number, number]
  /** Rotation in degrees */
  rotationDeg?: number
}

/**
 * Render a single-unit building from generated config
 */
export function renderSingleUnitBuilding(
  building: GeneratedBuilding,
  units: GeneratedUnit[],
  options: BuildingRenderOptions = {}
): THREE.Group {
  const {
    buildingId,
    buildingName,
    position = [0, 0, 0],
    rotationDeg,
    ...unitOptions
  } = options

  // Validate we have exactly one unit
  if (units.length !== 1) {
    console.warn(`SingleUnitOrchestrator: Expected 1 unit, got ${units.length}`)
  }

  const group = new THREE.Group()
  group.name = `building-${buildingId || building.code}`
  group.userData = {
    buildingId: buildingId || building.code,
    buildingName: buildingName || building.name,
    buildingType: building.type,
    template: 'single_unit_community',
  }

  // Get colors from building settings
  const floorColor = building.settings?.floor_color || unitOptions.floorColor
  const wallColor = building.settings?.wall_color || unitOptions.wallColor

  // Render the single unit
  const unit = units[0]
  if (unit) {
    const unitGroup = renderUnit(unit, {
      ...unitOptions,
      floorColor,
      wallColor,
    })

    // Shift unit to center it on building origin (like SingleRowOrchestrator does)
    // Without this, the unit center is at width/2, making the building origin at its left edge
    const buildingCenterX = building.exterior_width / 2
    unitGroup.position.x -= buildingCenterX * FEET_TO_METERS

    group.add(unitGroup)
  }

  // Apply building rotation
  const rotation = rotationDeg ?? (building.rotation * 180 / Math.PI)  // Convert radians to degrees
  if (rotation !== 0) {
    group.rotation.y = (rotation * Math.PI) / 180  // Convert back to radians
  }

  // Apply building position (convert feet to meters)
  group.position.set(
    position[0] * FEET_TO_METERS,
    position[1] * FEET_TO_METERS,
    position[2] * FEET_TO_METERS
  )

  return group
}

/**
 * Render a single-unit building from database-loaded config.
 * Convenience wrapper that extracts building/unit data from a combined object.
 *
 * Named distinctly from the template-aware dispatcher in `../renderBuildingFromConfig`
 * — both were previously called `renderBuildingFromConfig`, which made the name
 * ambiguous through the `rendering/index.ts` barrel (TS2308).
 */
export function renderSingleUnitBuildingFromConfig(
  config: {
    building: GeneratedBuilding
    units: GeneratedUnit[]
    position: [number, number, number]
    rotation?: number
    buildingId?: string
  },
  options: Omit<BuildingRenderOptions, 'position' | 'rotationDeg' | 'buildingId'> = {}
): THREE.Group {
  return renderSingleUnitBuilding(
    config.building,
    config.units,
    {
      ...options,
      buildingId: config.buildingId,
      buildingName: config.building.name,
      position: config.position,
      rotationDeg: config.rotation,
    }
  )
}
